import { forwardRef, useCallback, useState } from "react";

import { Box } from "@mui/material";
import Card from "@mui/material/Card";
import CardMedia from "@mui/material/CardMedia";
import Menu from "@mui/material/Menu";
import { PopoverOrigin } from "@mui/material/Popover";
import { SxProps, Theme, useTheme } from "@mui/material/styles";

import { DashboardButton } from "../DashboardButton/DashboardButton";
import { DashboardMenuItem } from "../MenuItem";

import { CardProps, DEFAULT_CARD_VIBE } from "./types";
import type { CardVibe } from "./types";

import { mergeSx } from "@/common/helpers/mergeSx";
import { DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { MoreVertical } from "@/icons/outlined";

const anchorOrigin: PopoverOrigin = {
  vertical: "bottom",
  horizontal: "left",
};

const transformOrigin: PopoverOrigin = {
  vertical: "top",
  horizontal: "left",
};

function getRootSx(
  theme: Theme,
  props: {
    active?: boolean;
    disabled?: boolean;
    fullWidth?: boolean;
    noHover?: boolean;
    noBackgroundOnHover?: boolean;
    imageSrc?: string;
    onClick?: CardProps["onClick"];
    vibe?: CardVibe;
  }
): SxProps<Theme> {
  const {
    active,
    disabled,
    fullWidth,
    noHover,
    noBackgroundOnHover,
    imageSrc,
    onClick,
    vibe = DEFAULT_CARD_VIBE,
  } = props;

  const activeBg = theme.palette.surface[vibe].quiet;
  const activeBorderColor =
    vibe === "positive" ? theme.palette.border.neutral.moderate : theme.palette.border.brand.intense;
  const hoverBg = vibe === "brand" ? theme.palette.surface.neutral.supershy : theme.palette.surface[vibe].quiet;
  const defaultBg = theme.palette.background.paper;
  const disabledBg = theme.palette.surface.neutral.quiet;

  return {
    border: "1px solid",
    borderColor: theme.palette.border.neutral.quiet,
    backgroundColor: disabled ? disabledBg : defaultBg,
    width: "100%",
    maxWidth: fullWidth ? "unset" : 360,
    padding: theme.spacing(2),
    boxShadow: "none",
    transition: "background-color 0.2s ease-in-out, border-color 0.2s ease-in-out",
    ...(active && {
      backgroundColor: activeBg,
      borderColor: activeBorderColor,
    }),
    ...(!(noHover || imageSrc) &&
      !disabled && {
        "&:hover": {
          backgroundColor: hoverBg,
          ...(active && { borderColor: activeBorderColor }),
        },
        "&:focus": {
          backgroundColor: activeBg,
          ...(active && { borderColor: activeBorderColor }),
        },
      }),
    ...(noBackgroundOnHover && {
      "&:hover": {
        backgroundColor: "unset",
        borderColor: theme.palette.border.neutral.moderate,
      },
    }),
    ...(disabled && {
      cursor: "default",
    }),
    ...(Boolean(onClick) &&
      !disabled && {
        cursor: "pointer",
      }),
  };
}

const InnerDashboardCardSx = forwardRef<HTMLDivElement, CardProps>(
  (
    {
      active,
      avatar,
      children,
      vibe = DEFAULT_CARD_VIBE,
      footerActions,
      fullWidth = false,
      headerActions,
      imageSrc,
      noHover,
      noBackgroundOnHover,
      disabled = false,
      onClick,
      subtitle,
      title,
      component = "div",
      sx,
      ...rest
    },
    ref
  ) => {
    const theme = useTheme();
    const [anchorToolMenu, setAnchorToolMenu] = useState<null | HTMLElement>(null);

    const onMenuClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
      setAnchorToolMenu(event.currentTarget);
    }, []);

    const onClose = useCallback(() => setAnchorToolMenu(null), []);

    const hasHeader = title || subtitle || avatar || (headerActions && headerActions.length > 0);

    const rootSx = getRootSx(theme, {
      active,
      disabled,
      fullWidth,
      noHover,
      noBackgroundOnHover,
      imageSrc,
      onClick,
      vibe,
    });

    return (
      <Card {...rest} ref={ref} component={component} onClick={disabled ? undefined : onClick} sx={mergeSx(rootSx, sx)}>
        {imageSrc && (
          <CardMedia
            image={imageSrc}
            sx={{
              height: 240,
              padding: theme.spacing(1),
              width: "100%",
            }}
          />
        )}
        {hasHeader && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: theme.spacing(2, 0),
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
              }}
            >
              {avatar && <Box sx={{ marginRight: theme.spacing(2) }}>{avatar}</Box>}
              {(title || subtitle) && (
                <div>
                  {title && (
                    <DashboardTypography
                      variant="h6"
                      sx={{
                        color: theme.palette.text.neutral.intense,
                        fill: theme.palette.text.neutral.intense,
                      }}
                    >
                      {title}
                    </DashboardTypography>
                  )}
                  {subtitle && (
                    <DashboardTypography variant="caption" sx={{ color: theme.palette.text.neutral.quiet }}>
                      {subtitle}
                    </DashboardTypography>
                  )}
                </div>
              )}
            </Box>
            {headerActions && headerActions.length > 0 && (
              <DashboardIconButton vibe="neutral" onClick={onMenuClick} size="large">
                <MoreVertical />
              </DashboardIconButton>
            )}
          </Box>
        )}
        {children && <Box>{children}</Box>}
        {footerActions && footerActions.length > 0 && (
          <Box
            sx={{
              padding: theme.spacing(2, 0),
              "& button": {
                marginLeft: theme.spacing(1),
              },
              "& button:first-child": {
                marginLeft: 0,
              },
            }}
          >
            {footerActions?.map((buttonProps) => (
              <DashboardButton
                key={buttonProps.children?.toString()}
                vibe="brand"
                size="small"
                variant="text"
                {...buttonProps}
              />
            ))}
          </Box>
        )}

        <Menu
          anchorOrigin={anchorOrigin}
          anchorEl={anchorToolMenu}
          transformOrigin={transformOrigin}
          open={Boolean(anchorToolMenu)}
          onClick={onClose}
          onClose={onClose}
        >
          {headerActions?.map(({ children, onClick }) => (
            <DashboardMenuItem key={children?.toString()} onClick={onClick}>
              {children}
            </DashboardMenuItem>
          ))}
        </Menu>
      </Card>
    );
  }
);

InnerDashboardCardSx.displayName = "InnerDashboardCardSx";

export const DashboardCardSx = forwardRef<HTMLDivElement, CardProps>((props, ref) => (
  <InnerDashboardCardSx {...props} ref={ref} />
));

DashboardCardSx.displayName = "DashboardCardSx";
